import { useAuthStore } from '@/stores';
import { User, GoogleAuthResponse, LoginCredentials, Permission } from '@/types';
import { useGoogleAuth, useEmailAuth } from './use-auth-queries';

export const useAuth = () => {
  const {
    user,
    isAuthenticated,
    setUser,
    setGoogleAccessToken,
    setAuthenticated,
    clearAuth,
    logout
  } = useAuthStore();

  // TanStack Query hooks
  const googleAuthMutation = useGoogleAuth();
  const emailAuthMutation = useEmailAuth();

  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;
    return user.permissions.includes(permission);
  };

  const hasRole = (role: string): boolean => {
    if (!user) return false;
    return user.role === role;
  };

  const login = (userData: User): void => {
    setUser(userData);
    setAuthenticated(true);
  };

  const signOut = async (): Promise<void> => {
    await logout();
  };

  const handleGoogleAuth = async (response: GoogleAuthResponse): Promise<void> => {
    try {
      setGoogleAccessToken(response.credential);

      // Use TanStack Query mutation
      const result = await googleAuthMutation.mutateAsync(response);

      // Update store with the result (cookie is set by server)
      setUser(result.user);
      setAuthenticated(true);
    } catch (error) {
      clearAuth();
      throw error;
    }
  };


  const handleEmailAuth = async (credentials: LoginCredentials): Promise<void> => {
    try {
      const result = await emailAuthMutation.mutateAsync(credentials);
      setUser(result.user);
      setAuthenticated(true);
    } catch (error) {
      clearAuth();
      throw error;
    }
  };

  return {
    user,
    isAuthenticated,
    isLoading: googleAuthMutation.isPending || emailAuthMutation.isPending,
    hasPermission,
    hasRole,
    login,
    signOut,
    handleGoogleAuth,
    handleEmailAuth,
  };
};
